/**
 * 把项目的 dynamic_fields 转换成"列表卡片"上展示的简洁徽章。
 *
 * 规则：
 * - 按预定优先级输出（地区、招标方、年份…），保证视觉一致；
 * - 自动跳过对象 / 数组类字段（packages/winners/remarks 等）以及内部统计字段；
 * - 金额、计数等数值字段会被格式化为人类可读形式；
 * - 字段值为空、null、空字符串则不展示；
 * - winner 是首位中标人，单独展示一家公司在列表里意义不大且会误导，改用 winner_count；
 * - 默认最多输出 5 条，避免行卡片被撑爆。
 */
export type FieldBadge = { key: string; label: string; value: string };

const LABELS: Record<string, string> = {
  year: "年份",
  province: "省份",
  region: "地区",
  city: "城市",
  type: "公告类型",
  category: "采购类型",
  pur_type: "采购类型",
  owner_type: "招标方类型",
  bidder: "招标方",
  amount: "金额",
  amount_wan: "金额",
};

/** 这些字段不应该出现在列表徽章里 */
const SKIP_KEYS = new Set([
  "packages", "winners", "remarks", "items",
  "content", "content_html", "summary",
  // winner 在列表中误导性强（只显示了首位中标人），改用 winner_count
  "winner",
]);

/** 渲染顺序：越靠前的越优先展示 */
const ORDER = [
  "year", "province", "region", "city",
  "type", "pur_type", "category", "owner_type",
  "bidder",
  "winner_count",
  "amount_wan", "amount",
  "package_count",
];

function fmtAmountWan(n: number): string {
  if (!isFinite(n) || n <= 0) return "";
  if (n >= 10000) return `${(n / 10000).toFixed(2).replace(/\.?0+$/, "")} 亿元`;
  if (n < 1) return `${(n * 10000).toFixed(0)} 元`;
  return `${n.toFixed(2).replace(/\.?0+$/, "")} 万元`;
}

function fmtAmount(n: number): string {
  if (!isFinite(n) || n <= 0) return "";
  if (n >= 1e8) return `${(n / 1e8).toFixed(2).replace(/\.?0+$/, "")} 亿元`;
  if (n >= 1e4) return `${(n / 1e4).toFixed(2).replace(/\.?0+$/, "")} 万元`;
  return `${n.toFixed(0)} 元`;
}

export function summarizeDynamicFields(df: any, max = 5): FieldBadge[] {
  if (!df || typeof df !== "object") return [];

  // 自动从 winners 数组推导 winner_count（兼容旧数据）
  const enriched: any = { ...df };
  if (enriched.winner_count === undefined && Array.isArray(enriched.winners)) {
    enriched.winner_count = enriched.winners.length;
  }

  const out: FieldBadge[] = [];
  const seen = new Set<string>();

  const push = (key: string) => {
    if (seen.has(key) || SKIP_KEYS.has(key)) return;
    const v = enriched[key];
    if (v === null || v === undefined || v === "") return;
    if (typeof v === "object") return;

    const label = LABELS[key] || "";
    let value: string;

    if (key === "amount_wan" && typeof v === "number") {
      const s = fmtAmountWan(v);
      if (!s) return;
      value = s;
    } else if (key === "amount" && typeof v === "number") {
      const s = fmtAmount(v);
      if (!s) return;
      value = s;
    } else if (key === "package_count") {
      const n = Number(v);
      if (!isFinite(n) || n <= 0) return;
      value = `${n} 个分包`;
    } else if (key === "winner_count") {
      const n = Number(v);
      if (!isFinite(n) || n <= 0) return;
      value = `${n} 家中标`;
    } else {
      value = String(v).trim();
      if (!value) return;
      if (value.length > 28) value = value.slice(0, 27) + "…";
    }

    seen.add(key);
    out.push({ key, label, value });
  };

  for (const k of ORDER) {
    if (out.length >= max) break;
    if (k in enriched) push(k);
  }

  if (out.length < max) {
    for (const k of Object.keys(enriched)) {
      if (out.length >= max) break;
      if (seen.has(k) || SKIP_KEYS.has(k) || k.endsWith("_html")) continue;
      const v = enriched[k];
      if (v === null || v === undefined || v === "" || typeof v === "object") continue;
      push(k);
    }
  }

  return out;
}
